import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { fetchProduct, joinWaitlist } from "../lib/api";
import { useCart } from "../context/CartContext";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";

const ImagePlaceholder = () => (
    <div className="w-full h-full flex flex-col items-center justify-center bg-[#FBF9F6] text-[#0A2342] p-12">
        <div className="font-serif-display text-7xl md:text-8xl leading-none">
            C&amp;O
        </div>
        <div className="mt-6 w-14 h-px bg-[#9E5A35]" />
        <div className="mt-4 text-[11px] tracking-[0.35em] uppercase text-[#5C6A79] text-center">
            Design in progress
        </div>
    </div>
);

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(true);
    const [activeIdx, setActiveIdx] = useState(0);

    const gallery = product?.gallery && product.gallery.length > 0
        ? product.gallery
        : product?.image_url
          ? [product.image_url, ...(product.image_url_back ? [product.image_url_back] : [])]
          : [];
    const activeImage = gallery[activeIdx] || null;

    useEffect(() => {
        fetchProduct(id)
            .then((p) => {
                setProduct(p);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                navigate("/shop");
            });
    }, [id, navigate]);

    if (loading)
        return (
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-32 text-center text-[#5C6A79]">
                Loading…
            </div>
        );
    if (!product) return null;

    const isAvailable = product.status === "available";

    const handleAdd = () => {
        addItem(product, qty);
        toast.success(`${product.name} added to cart`);
    };

    const handleWaitlist = async (e) => {
        e.preventDefault();
        if (!email) return;
        try {
            await joinWaitlist({ email, product_id: product.id });
            toast.success("You're on the waitlist. We'll be in touch.");
            setEmail("");
        } catch {
            toast.error("Could not join waitlist. Try again.");
        }
    };

    return (
        <div
            data-testid="page-product-detail"
            className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-10 md:py-16"
        >
            <Link
                data-testid="product-back-link"
                to="/shop"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#5C6A79] hover:text-[#0A2342] mb-10"
            >
                <ArrowLeft size={14} /> Back to Shop
            </Link>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
                <div>
                    <div className="bg-white border border-[#E5E0D8] rounded-sm flex items-center justify-center min-h-[500px] overflow-hidden aspect-[4/5]">
                        {activeImage ? (
                            <img
                                data-testid="product-image"
                                src={activeImage}
                                alt={product.name}
                                className={`w-full h-full transition-opacity duration-300 ${activeIdx <= 1 ? "object-contain p-10 md:p-16" : "object-cover"}`}
                            />
                        ) : (
                            <ImagePlaceholder />
                        )}
                    </div>
                    {gallery.length > 1 && (
                        <div
                            data-testid="product-image-toggle"
                            className="mt-4 flex items-center gap-2 flex-wrap"
                        >
                            {gallery.map((img, idx) => (
                                <button
                                    key={img}
                                    data-testid={idx === 0 ? "product-view-front" : idx === 1 ? "product-view-back" : `product-view-${idx}`}
                                    onClick={() => setActiveIdx(idx)}
                                    className={`w-16 h-20 border rounded-sm overflow-hidden bg-white flex items-center justify-center transition-all ${
                                        activeIdx === idx
                                            ? "border-[#0A2342] ring-1 ring-[#0A2342]"
                                            : "border-[#E5E0D8] opacity-60 hover:opacity-100"
                                    }`}
                                    aria-label={`View image ${idx + 1}`}
                                >
                                    <img
                                        src={img}
                                        alt={`view ${idx + 1}`}
                                        className={`w-full h-full ${idx <= 1 ? "object-contain p-1.5" : "object-cover"}`}
                                    />
                                </button>
                            ))}
                            {product.image_url_back && (
                                <span className="ml-2 text-[10px] tracking-[0.22em] uppercase text-[#5C6A79]">
                                    Reversible
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className="md:sticky md:top-28 md:self-start">
                    {!isAvailable && (
                        <span className="inline-block bg-[#0A2342] text-[#FBF9F6] text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 mb-6">
                            Coming Soon
                        </span>
                    )}
                    <h1
                        data-testid="product-title"
                        className="font-serif-display text-4xl md:text-5xl text-[#0A2342] leading-[1.05]"
                    >
                        {product.name}
                    </h1>
                    <div
                        data-testid="product-price"
                        className="mt-4 text-2xl text-[#0A2342] font-medium"
                    >
                        ${product.price.toFixed(2)}
                    </div>
                    <p className="mt-6 text-base leading-relaxed text-[#1A1A1A]/80">
                        {product.description}
                    </p>

                    {isAvailable ? (
                        <div className="mt-10">
                            <div className="flex items-center gap-6 mb-6">
                                <span className="text-[11px] tracking-[0.22em] uppercase text-[#5C6A79]">
                                    Quantity
                                </span>
                                <div className="inline-flex items-center border border-[#E5E0D8] rounded-sm">
                                    <button
                                        data-testid="pd-qty-decrease"
                                        onClick={() =>
                                            setQty((q) => Math.max(1, q - 1))
                                        }
                                        className="p-2 text-[#0A2342]"
                                        aria-label="Decrease quantity"
                                    >
                                        <Minus size={14} />
                                    </button>
                                    <span
                                        data-testid="pd-qty-value"
                                        className="px-4 text-sm"
                                    >
                                        {qty}
                                    </span>
                                    <button
                                        data-testid="pd-qty-increase"
                                        onClick={() => setQty((q) => q + 1)}
                                        className="p-2 text-[#0A2342]"
                                        aria-label="Increase quantity"
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>
                            <button
                                data-testid="pd-add-to-cart"
                                onClick={handleAdd}
                                className="btn-primary w-full justify-center"
                            >
                                Add to Cart · ${(product.price * qty).toFixed(2)}
                            </button>
                        </div>
                    ) : (
                        <form
                            data-testid="pd-waitlist-form"
                            onSubmit={handleWaitlist}
                            className="mt-10"
                        >
                            <label className="text-[11px] tracking-[0.22em] uppercase text-[#5C6A79]">
                                Join the waitlist
                            </label>
                            <div className="mt-3 flex gap-3 flex-col sm:flex-row">
                                <input
                                    data-testid="pd-waitlist-email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="flex-1 bg-transparent border border-[#E5E0D8] px-4 py-3 text-sm focus:outline-none focus:border-[#0A2342]"
                                />
                                <button
                                    data-testid="pd-waitlist-submit"
                                    type="submit"
                                    className="btn-primary justify-center"
                                >
                                    Notify Me
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="mt-12">
                        <Accordion type="single" collapsible>
                            <AccordionItem value="details">
                                <AccordionTrigger
                                    data-testid="pd-accordion-details"
                                    className="text-[11px] tracking-[0.22em] uppercase text-[#0A2342]"
                                >
                                    Details
                                </AccordionTrigger>
                                <AccordionContent>
                                    <ul className="space-y-2 text-sm text-[#1A1A1A]/80 pt-2">
                                        {product.details?.map((d) => (
                                            <li
                                                key={d}
                                                className="flex gap-3"
                                            >
                                                <span className="w-4 h-px bg-[#9E5A35] mt-2.5 shrink-0" />
                                                {d}
                                            </li>
                                        ))}
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                            <AccordionItem value="shipping">
                                <AccordionTrigger
                                    data-testid="pd-accordion-shipping"
                                    className="text-[11px] tracking-[0.22em] uppercase text-[#0A2342]"
                                >
                                    Shipping &amp; Returns
                                </AccordionTrigger>
                                <AccordionContent>
                                    <p className="text-sm text-[#1A1A1A]/80 pt-2">
                                        Every fob ships within 5–8 business days
                                        from our workshop. Free U.S. shipping on
                                        orders over $50. 30-day satisfaction
                                        guarantee on all in-stock items.
                                    </p>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;
